import { UsergroupAddOutlined } from '@ant-design/icons';
import { ConfigProvider, Select } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type CollaboratorChooserProps, CollaboratorChooser } from './CollaboratorChooser';

interface CollaboratorChooserAppendableProps extends Omit<CollaboratorChooserProps, 'onChange'> {
  onChange: (params: {
    collaborators: string[];
    collaboratorUpdateAction: string | undefined;
  }) => void;
}

export const CollaboratorChooserAppendable = ({
  value: _value,
  defaultValue: _defaultValue,
  ...props
}: CollaboratorChooserAppendableProps) => {
  const { t } = useTranslation();
  const [collaboratorUpdateAction, setCollaboratorUpdateAction] = useState<string>();
  const [collaborators, setCollaborators] = useState<string[]>([]);
  const { disabled, onChange } = props;

  const actionOptions = [
    {
      label: t('collaborator.addNewCollaborators', 'Add new collaborators'),
      value: 'APPEND',
    },
    {
      label: t('collaborator.replaceCurrentCollaborators', 'Replace current collaborators'),
      value: 'REPLACE',
    },
    {
      label: t('collaborator.removeCurrentCollaborators', 'Remove current collaborators'),
      value: 'REMOVE',
    },
  ];

  const emptyRender = () => (
    <div>
      <UsergroupAddOutlined style={{ marginRight: '4px' }} />
      <span>{t('collaborator.noCurrentCollaborators', 'No current collaborators')}</span>
    </div>
  );

  const handleWideChange = (
    collaboratorUpdateAction: string | undefined,
    collaborators: string[]
  ) => {
    if (onChange) {
      onChange({ collaborators, collaboratorUpdateAction });
    }
  };

  const handleActionChange = (action: string) => {
    setCollaboratorUpdateAction(action);
    handleWideChange(action, collaborators);
  };

  const handleCollaboratorChange = (collaborators: string[]) => {
    setCollaborators(collaborators);
    handleWideChange(collaboratorUpdateAction, collaborators);
  };

  return (
    <ConfigProvider renderEmpty={emptyRender}>
      <Select
        value={collaboratorUpdateAction}
        onChange={(action) => handleActionChange(action)}
        disabled={disabled}
        style={{ margin: '0 0 4px 0' }}
        placeholder={t('collaborator.selectEditOption', 'Select an edit option')}
        options={actionOptions}
      />
      <CollaboratorChooser
        hidden={
          collaboratorUpdateAction && ['APPEND', 'REPLACE'].includes(collaboratorUpdateAction)
            ? false
            : true
        }
        onChange={(collaborators) => handleCollaboratorChange(collaborators)}
      />
    </ConfigProvider>
  );
};
